import { useEffect, useState } from "react";
import api from "../api";

function CheckinModal({ reservation, onClose }) {

  const [loading, setLoading] = useState(true);

  const [duplicateLoading, setDuplicateLoading] =
    useState(false);

  const [duplicateCheck, setDuplicateCheck] =
    useState(null);

  // ======================================================
  // DÉCISION DU RÉCEPTIONNISTE
  // ======================================================

  const [duplicateDecision, setDuplicateDecision] =
    useState(null);

  const [processing, setProcessing] =
    useState(false);

  // ======================================================
  // FORMULAIRE CLIENT
  // ======================================================

  const [form, setForm] = useState({
    nom: "",
    post_nom: "",
    prenom: "",
    telephone: "",
    email: "",
    nationalite: "",
    sexe: "",
    adresse: "",
    type_piece: "",
    numero_piece: ""
  });

  const [provenance, setProvenance] = useState("");
  const [destination, setDestination] = useState("");

  // ======================================================
  // CHARGEMENT
  // ======================================================

  useEffect(() => {
    loadClient();
  }, []);

  const loadClient = async () => {

    try {

      // ====================================================
      // CLIENT DE LA RÉSERVATION
      // ====================================================

      const res = await api.get(
        `/clients/${reservation.client_id}`
      );

      setForm({
        nom: res.data.nom || "",
        post_nom: res.data.post_nom || "",
        prenom: res.data.prenom || "",
        telephone: res.data.telephone || "",
        email: res.data.email || "",
        nationalite: res.data.nationalite || "",
        sexe: res.data.sexe || "",
        adresse: res.data.adresse || "",
        type_piece: res.data.type_piece || "",
        numero_piece: res.data.numero_piece || ""
      });

      // ====================================================
      // DÉTECTION DES CLIENTS SIMILAIRES
      // ====================================================

      try {

        setDuplicateLoading(true);

        const duplicateRes = await api.get(
          `/clients/${reservation.client_id}/doublons`
        );

        setDuplicateCheck(
          duplicateRes.data
        );

      } catch (err) {

        console.error(
          "Erreur détection doublons :",
          err
        );

        setDuplicateCheck(null);

      } finally {

        setDuplicateLoading(false);

      }

    } catch (err) {

      console.error(err);

      alert(
        "Impossible de charger le client"
      );

    } finally {

      setLoading(false);

    }

  };

  // ======================================================
  // CHANGEMENT FORMULAIRE
  // ======================================================

  const handleChange = (e) => {

    const {
      name,
      value
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value
    }));

  };

  // ======================================================
  // DOSSIER COMPLET
  // ======================================================

  const dossierComplet =
    form.nationalite &&
    form.sexe &&
    form.adresse &&
    form.type_piece &&
    form.numero_piece;

  // ======================================================
  // CHOIX : UTILISER UNE FICHE EXISTANTE
  // ======================================================

  const choisirFicheExistante = (client) => {

    setDuplicateDecision({
      type: "conserver",
      clientId: client.id,
      client
    });

  };

  // ======================================================
  // CHOIX : METTRE À JOUR UNE FICHE EXISTANTE
  // ======================================================

  const choisirMiseAJour = (client) => {

    setDuplicateDecision({
      type: "mettre_a_jour",
      clientId: client.id,
      client
    });

  };

  // ======================================================
  // CHOIX : CONSERVER COMME NOUVEAU CLIENT
  // ======================================================

  const choisirNouveauClient = () => {

    setDuplicateDecision({
      type: "nouveau",
      clientId: null,
      client: null
    });

  };

  // ======================================================
  // LIBELLÉ DU BOUTON PRINCIPAL
  // ======================================================

  const getCheckinButtonLabel = () => {

    if (processing) {
      return "Traitement en cours...";
    }

    if (
      duplicateCheck?.found &&
      duplicateCheck?.matches?.length > 0
    ) {

      if (!duplicateDecision) {
        return "Choisir une action";
      }

      if (
        duplicateDecision.type ===
        "conserver"
      ) {
        return `Unifier avec le client #${duplicateDecision.clientId}`;
      }

      if (
        duplicateDecision.type ===
        "mettre_a_jour"
      ) {
        return `Mettre à jour le client #${duplicateDecision.clientId}`;
      }

      if (
        duplicateDecision.type ===
        "nouveau"
      ) {
        return "Conserver comme nouveau client";
      }

    }

    return "Valider le check-in";

  };

  // ======================================================
  // CHECK-IN
  // ======================================================

  const handleCheckin = async () => {

    if (!dossierComplet) {

      alert(
        "Veuillez compléter toutes les informations obligatoires."
      );

      return;

    }

    // ====================================================
    // UNE CORRESPONDANCE EXISTE :
    // UNE DÉCISION EST OBLIGATOIRE
    // ====================================================

    if (
      duplicateCheck?.found &&
      duplicateCheck?.matches?.length > 0 &&
      !duplicateDecision
    ) {

      alert(
        "Veuillez choisir une action pour le client avant de continuer."
      );

      return;

    }

    try {

      setProcessing(true);

      // ==================================================
      // CAS 1 : UNIFIER AVEC UNE FICHE EXISTANTE
      // ==================================================

      if (
        duplicateDecision?.type ===
        "conserver"
      ) {

        await api.post(
          "/clients/fusionner",
          {
            client_source_id:
              reservation.client_id,

            client_cible_id:
              duplicateDecision.clientId,

            action: "conserver"
          }
        );

      }

      // ==================================================
      // CAS 2 : METTRE À JOUR LA FICHE EXISTANTE
      // ==================================================

      else if (
        duplicateDecision?.type ===
        "mettre_a_jour"
      ) {

        await api.post(
          "/clients/fusionner",
          {
            client_source_id:
              reservation.client_id,

            client_cible_id:
              duplicateDecision.clientId,

            action: "mettre_a_jour"
          }
        );

      }

      // ==================================================
      // CAS 3 : NOUVEAU CLIENT
      // ==================================================

      else {

        await api.put(
          `/clients/${reservation.client_id}`,
          form
        );

      }

      // ==================================================
      // CHECK-IN
      // ==================================================

      await api.post(
        `/sejours/checkin/${reservation.id}`
      );

      // ==================================================
      // 📄 GÉNÉRATION DE LA FICHE DE SÉJOUR
      // ==================================================

      try {

        const pdfResponse = await api.post(
          `/sejours/${reservation.id}/fiche-sejour-pdf`,
          {
            provenance,
            destination,
            lang: "fr"
          },
          {
            responseType: "blob"
          }
        );

        const blob = new Blob(
          [pdfResponse.data],
          {
            type: "application/pdf"
          }
        );

        const url = window.URL.createObjectURL(blob);

        window.open(
          url,
          "_blank",
          "noopener,noreferrer"
        );

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 60000);

      } catch (pdfError) {

        console.error(
          "Erreur génération fiche de séjour :",
          pdfError
        );

        alert(
          "Check-in effectué, mais impossible de générer la fiche de séjour."
        );
      }

      alert(
        duplicateDecision?.type === "conserver"
          ? "Client unifié et check-in effectué avec succès"
          : duplicateDecision?.type === "mettre_a_jour"
            ? "Fiche client mise à jour et check-in effectué avec succès"
            : "Arrivée enregistrée avec succès"
      );

      onClose();

    } catch (err) {

      console.error(
        "Erreur check-in :",
        err
      );

      alert(
        err?.response?.data?.error ||
        "Erreur lors du check-in"
      );

    } finally {

      setProcessing(false);

    }

  };

  // ======================================================
  // CHARGEMENT
  // ======================================================

  if (loading) {

    return (
      <div className="p-6">
        Chargement...
      </div>
    );

  }

  // ======================================================
  // INTERFACE
  // ======================================================

  return (

    <div className="bg-white rounded-2xl p-5 space-y-5 max-w-2xl w-full">

      {/* ================================================== */}
      {/* TITRE */}
      {/* ================================================== */}

      <div>

        <h2 className="text-xl font-bold">
          Check-in du client
        </h2>

        <p className="text-sm text-slate-500 mt-1">
          Vérification et validation du dossier client
        </p>

      </div>

      {/* ================================================== */}
      {/* INFORMATIONS RÉSERVATION */}
      {/* ================================================== */}

      <div>

        <h3 className="font-semibold mb-3">
          Informations réservation
        </h3>

        <div className="grid md:grid-cols-2 gap-3">

          <input
            value={form.nom}
            disabled
            className="border rounded-xl px-4 py-3 bg-gray-100"
          />

          <input
            value={form.post_nom}
            disabled
            className="border rounded-xl px-4 py-3 bg-gray-100"
          />

          <input
            value={form.prenom}
            disabled
            className="border rounded-xl px-4 py-3 bg-gray-100"
          />

          <input
            value={form.telephone}
            disabled
            className="border rounded-xl px-4 py-3 bg-gray-100"
          />

          <input
            value={form.email}
            disabled
            className="border rounded-xl px-4 py-3 bg-gray-100 md:col-span-2"
          />

        </div>

      </div>

      {/* ================================================== */}
      {/* DÉTECTION CLIENT SIMILAIRE */}
      {/* ================================================== */}

      {duplicateLoading && (

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">

          <div className="flex items-center gap-2 text-slate-600">

            <span className="animate-pulse">
              🔍
            </span>

            <span className="font-medium">
              Vérification des données client...
            </span>

          </div>

        </div>

      )}

      {/* ================================================== */}
      {/* AUCUN DOUBLON */}
      {/* ================================================== */}

      {!duplicateLoading &&
        duplicateCheck &&
        !duplicateCheck.found && (

          <div className="rounded-2xl border border-green-200 bg-green-50 p-4">

            <div className="flex items-center gap-2">

              <span className="text-green-600">
                ✓
              </span>

              <div>

                <p className="font-semibold text-green-800">
                  Aucun client correspondant trouvé
                </p>

                <p className="text-sm text-green-700 mt-1">
                  Ce client peut être enregistré comme nouveau client.
                </p>

              </div>

            </div>

          </div>

        )}

      {/* ================================================== */}
      {/* DOUBLONS */}
      {/* ================================================== */}

      {!duplicateLoading &&
        duplicateCheck?.found &&
        duplicateCheck?.matches?.length > 0 && (

          <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 space-y-4">

            <div>

              <h3 className="font-bold text-orange-800">

                ⚠️ Correspondance client détectée

              </h3>

              <p className="text-sm text-orange-700 mt-1">

                {duplicateCheck.matches.length} fiche
                {duplicateCheck.matches.length > 1 ? "s" : ""}
                possèdent les mêmes nom, post-nom et prénom.

              </p>

              <p className="text-xs text-orange-600 mt-2">

                Le rapprochement doit être confirmé par le réceptionniste.

              </p>

            </div>

            {/* ==================================================
                LISTE DES CLIENTS
            ================================================== */}

            {duplicateCheck.matches.map((client) => {

              const selected =
                duplicateDecision?.clientId ===
                client.id;

              return (

                <div
                  key={client.id}
                  className={
                    selected
                      ? "rounded-xl bg-white border-2 border-blue-500 p-4 shadow-sm"
                      : "rounded-xl bg-white border border-orange-200 p-4"
                  }
                >

                  <div className="flex items-center justify-between mb-3">

                    <div>

                      <h4 className="font-bold text-slate-800">
                        Client #{client.id}
                      </h4>

                      <p className="text-sm text-slate-600">
                        {client.nom} {client.post_nom} {client.prenom}
                      </p>

                    </div>

                    {selected && (

                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                        Sélectionné
                      </span>

                    )}

                  </div>

                  {/* ==================================================
                      COMPARAISON
                  ================================================== */}

                  <div className="grid md:grid-cols-2 gap-3 text-sm">

                    <div>

                      <p className="text-slate-500">
                        Téléphone enregistré
                      </p>

                      <p
                        className={
                          client.differences?.telephone
                            ? "font-semibold text-orange-700"
                            : "font-medium text-slate-800"
                        }
                      >
                        {client.telephone || "Non renseigné"}
                      </p>

                      {client.differences?.telephone && (

                        <p className="text-xs text-orange-600 mt-1">
                          Différent de la réservation
                        </p>

                      )}

                    </div>

                    <div>

                      <p className="text-slate-500">
                        Email enregistré
                      </p>

                      <p
                        className={
                          client.differences?.email
                            ? "font-semibold text-orange-700"
                            : "font-medium text-slate-800"
                        }
                      >
                        {client.email || "Non renseigné"}
                      </p>

                      {client.differences?.email && (

                        <p className="text-xs text-orange-600 mt-1">
                          Différent de la réservation
                        </p>

                      )}

                    </div>

                  </div>

                  {/* ==================================================
                      DIFFÉRENCES
                  ================================================== */}

                  {client.hasDifferences && (

                    <div className="mt-3 rounded-xl bg-orange-50 border border-orange-100 p-3">

                      <p className="text-sm font-semibold text-orange-800 mb-2">
                        Différences détectées
                      </p>

                      <div className="space-y-1 text-sm">

                        {client.differences?.telephone && (

                          <p className="text-orange-700">
                            • Téléphone différent
                          </p>

                        )}

                        {client.differences?.email && (

                          <p className="text-orange-700">
                            • Email différent
                          </p>

                        )}

                      </div>

                    </div>

                  )}

                  {/* ==================================================
                      ACTIONS
                  ================================================== */}

                  <div className="grid md:grid-cols-2 gap-2 mt-4">

                    <button
                      type="button"
                      disabled={processing}
                      onClick={() =>
                        choisirFicheExistante(client)
                      }
                      className={
                        selected &&
                        duplicateDecision?.type === "conserver"
                          ? "bg-blue-600 text-white py-2.5 rounded-xl font-semibold"
                          : "bg-slate-100 text-slate-700 py-2.5 rounded-xl hover:bg-slate-200"
                      }
                    >
                      Utiliser cette fiche
                    </button>

                    <button
                      type="button"
                      disabled={processing}
                      onClick={() =>
                        choisirMiseAJour(client)
                      }
                      className={
                        selected &&
                        duplicateDecision?.type === "mettre_a_jour"
                          ? "bg-blue-600 text-white py-2.5 rounded-xl font-semibold"
                          : "bg-slate-100 text-slate-700 py-2.5 rounded-xl hover:bg-slate-200"
                      }
                    >
                      Mettre à jour cette fiche
                    </button>

                  </div>

                </div>

              );

            })}

            {/* ==================================================
                NOUVEAU CLIENT
            ================================================== */}

            <button
              type="button"
              disabled={processing}
              onClick={choisirNouveauClient}
              className={
                duplicateDecision?.type === "nouveau"
                  ? "w-full border-2 border-blue-500 bg-blue-50 text-blue-700 py-3 rounded-xl font-semibold"
                  : "w-full border border-slate-300 bg-white text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-50"
              }
            >
              {duplicateDecision?.type === "nouveau"
                ? "✓ Nouveau client sélectionné"
                : "Conserver comme nouveau client"}
            </button>

          </div>

        )}

      {/* ================================================== */}
      {/* INFORMATIONS À COMPLÉTER */}
      {/* ================================================== */}

      <div>

        <h3 className="font-semibold mb-3">
          Informations obligatoires
        </h3>

        <div className="grid md:grid-cols-2 gap-3">

          <input
            name="nationalite"
            value={form.nationalite}
            onChange={handleChange}
            placeholder="Nationalité"
            className="border rounded-xl px-4 py-3"
          />

          <select
            name="sexe"
            value={form.sexe}
            onChange={handleChange}
            className="border rounded-xl px-4 py-3"
          >

            <option value="">
              Sexe
            </option>

            <option value="Masculin">
              Masculin
            </option>

            <option value="Féminin">
              Féminin
            </option>

          </select>

          <input
            name="adresse"
            value={form.adresse}
            onChange={handleChange}
            placeholder="Adresse"
            className="border rounded-xl px-4 py-3 md:col-span-2"
          />

          <select
            name="type_piece"
            value={form.type_piece}
            onChange={handleChange}
            className="border rounded-xl px-4 py-3"
          >

            <option value="">
              Type de pièce
            </option>

            <option value="Carte d'identité">
              Carte d'identité
            </option>

            <option value="Carte d'électeur">
              Carte d'électeur
            </option>

            <option value="Passeport">
              Passeport
            </option>

            <option value="Permis de conduire">
              Permis de conduire
            </option>

            <option value="Carte de résident">
              Carte de résident
            </option>

            <option value="Autre">
              Autre
            </option>

          </select>

          <input
            name="numero_piece"
            value={form.numero_piece}
            onChange={handleChange}
            placeholder="Numéro de pièce"
            className="border rounded-xl px-4 py-3"
          />

        </div>

      </div>


      {/* ================================================== */}
      {/* INFORMATIONS POUR LA FICHE DE SÉJOUR */}
      {/* ================================================== */}

      <div>
        <h3 className="font-semibold mb-3">
          Informations du séjour
        </h3>

        <div className="grid md:grid-cols-2 gap-3">

          <input
            type="text"
            value={provenance}
            onChange={(e) => setProvenance(e.target.value)}
            placeholder="Lieu de provenance"
            className="border rounded-xl px-4 py-3"
          />

          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="Lieu de destination"
            className="border rounded-xl px-4 py-3"
          />

        </div>
      </div>
      

      {/* ================================================== */}
      {/* ÉTAT DOSSIER */}
      {/* ================================================== */}

      {dossierComplet ? (

        <div className="text-green-600 font-semibold">
          ✓ Dossier client complet
        </div>

      ) : (

        <div className="text-orange-600 font-semibold">
          Compléter les informations manquantes
        </div>

      )}

      {/* ================================================== */}
      {/* RÉSUMÉ DÉCISION */}
      {/* ================================================== */}

      {duplicateCheck?.found &&
        duplicateCheck?.matches?.length > 0 &&
        duplicateDecision && (

          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm">

            <p className="font-semibold text-blue-800">
              Décision sélectionnée
            </p>

            {duplicateDecision.type === "conserver" && (

              <p className="text-blue-700 mt-1">
                Le séjour sera rattaché au client #
                {duplicateDecision.clientId}.
              </p>

            )}

            {duplicateDecision.type === "mettre_a_jour" && (

              <p className="text-blue-700 mt-1">
                La fiche du client #
                {duplicateDecision.clientId}
                sera mise à jour avec les données vérifiées
                de la réservation.
              </p>

            )}

            {duplicateDecision.type === "nouveau" && (

              <p className="text-blue-700 mt-1">
                La réservation restera attachée au nouveau client.
              </p>

            )}

          </div>

        )}

      {/* ================================================== */}
      {/* BOUTONS */}
      {/* ================================================== */}

      <div className="flex gap-3">

        <button
          type="button"
          onClick={onClose}
          disabled={processing}
          className="flex-1 bg-gray-200 py-3 rounded-xl"
        >
          Annuler
        </button>

        <button
          type="button"
          onClick={handleCheckin}
          disabled={
            processing ||
            !dossierComplet ||
            (
              duplicateCheck?.found &&
              duplicateCheck?.matches?.length > 0 &&
              !duplicateDecision
            )
          }
          className={
            processing ||
            !dossierComplet ||
            (
              duplicateCheck?.found &&
              duplicateCheck?.matches?.length > 0 &&
              !duplicateDecision
            )
              ? "flex-1 bg-slate-300 text-slate-500 py-3 rounded-xl cursor-not-allowed"
              : "flex-1 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700"
          }
        >
          {getCheckinButtonLabel()}
        </button>

      </div>

    </div>

  );

}

export default CheckinModal;